/* 7AYAA — global behaviour.
   Header scroll state, mobile nav, and shared drawer plumbing.
   Vanilla only. Everything here is observer-driven; there are no scroll listeners. */
(function () {
  'use strict';

  /* ------------------------------------------------ HEADER STATE
     The header sits transparent over the hero and turns Chalk once the hero
     has scrolled past. Driven by an IntersectionObserver on a sentinel the
     header section prints directly after itself — never a scroll handler. */
  function initHeaderState() {
    var header = document.querySelector('[data-header]');
    if (!header) return;

    var resting = header.dataset.restingState || 'solid';
    if (resting !== 'over') return; // chalk and deep headers never change

    /* A header that does not stick scrolls out of view before the sentinel is
       ever crossed, so the transparent -> Chalk swap would only ever repaint an
       off-screen element. Keyed off the computed position rather than a flag,
       so restoring `position: sticky` in CSS brings the behaviour back with no
       change here. */
    if (getComputedStyle(header).position !== 'sticky') {
      header.dataset.state = resting;
      return;
    }

    var sentinel = document.querySelector('[data-header-sentinel]');
    if (!sentinel || !('IntersectionObserver' in window)) {
      header.dataset.state = 'solid';
      return;
    }

    new IntersectionObserver(
      function (entries) {
        header.dataset.state = entries[0].isIntersecting ? 'over' : 'solid';
      },
      { rootMargin: '0px' }
    ).observe(sentinel);
  }

  /* Publish the rendered header height so sticky offsets never hardcode a number.
     This writes --7-header-actual, NOT --7-header-h.

     --7-header-h is the design token and it is what sizes the header. Writing
     the measurement back into it creates a feedback loop: the inline value
     outranks the stylesheet, the header keeps sizing itself from its own last
     measurement, and the mobile 58px override can never take effect.

     Consumers that need the real pixel height (borders included) read
     var(--7-header-actual, var(--7-header-h)). */
  var headerResizeObserver = null;

  function measureHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;
    var h = Math.round(header.getBoundingClientRect().height);
    if (h > 0) document.documentElement.style.setProperty('--7-header-actual', h + 'px');

    /* Track the element, not the window. The header also changes height when
       the announcement bar wraps or a logo image finally decodes — neither of
       which fires a resize event. */
    if ('ResizeObserver' in window && !headerResizeObserver) {
      headerResizeObserver = new ResizeObserver(function () {
        var next = Math.round(header.getBoundingClientRect().height);
        if (next > 0) document.documentElement.style.setProperty('--7-header-actual', next + 'px');
      });
      headerResizeObserver.observe(header);
    }
  }

  /* Publish how much chrome sits above the hero, so the hero can cap itself to
     the first screen: height: min(<setting>, calc(100svh - var(--7-above-hero))).

     The hero's document offset is affected by the announcement bar and header,
     not by the height of the document itself. Observe only those elements so a
     hero height update cannot feed back into a root-level ResizeObserver. */
  var aboveHeroObserver = null;

  function measureAboveHero() {
    var hero = document.querySelector('.s7-hero');
    if (!hero) return;

    function write() {
      /* Document offset, not viewport offset — this has to be independent of
         the current scroll position. */
      var top = hero.getBoundingClientRect().top + (window.pageYOffset || 0);
      document.documentElement.style.setProperty(
        '--7-above-hero', Math.max(0, Math.round(top)) + 'px');
    }
    write();

    if ('ResizeObserver' in window && !aboveHeroObserver) {
      var bar = document.querySelector('[data-announce]');
      var header = document.querySelector('[data-header]');
      aboveHeroObserver = new ResizeObserver(write);
      if (bar) aboveHeroObserver.observe(bar);
      if (header) aboveHeroObserver.observe(header);
    }
  }

  /* -------------------------------------------------- ANNOUNCEMENTS
     Rotates the announcement bar. Pauses on hover and on keyboard focus so a
     message cannot slide away mid-read, and does not run at all under reduced
     motion — there the first message simply stays. */
  function initAnnouncements() {
    document.querySelectorAll('[data-announce]').forEach(function (bar) {
      var items = bar.querySelectorAll('[data-announce-item]');
      if (items.length < 2) return;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      var delay = (parseInt(bar.dataset.interval, 10) || 5) * 1000;
      var index = 0;
      var timer = null;

      function show(next) {
        items[index].classList.remove('is-current');
        index = (next + items.length) % items.length;
        items[index].classList.add('is-current');
      }

      function start() {
        if (timer) return;
        timer = setInterval(function () { show(index + 1); }, delay);
      }

      function stop() {
        clearInterval(timer);
        timer = null;
      }

      bar.addEventListener('mouseenter', stop);
      bar.addEventListener('mouseleave', start);
      bar.addEventListener('focusin', stop);
      bar.addEventListener('focusout', start);

      /* A background tab should not burn through the rotation. */
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop();
        else start();
      });

      start();
    });
  }

  /* ------------------------------------------------------------ REVEAL
     Staggered entrance for [data-reveal] groups.

     Three deliberate safeguards, because a reveal that fails leaves content
     invisible — a far worse outcome than no animation at all:

       1. The hidden state is applied by JS (`is-armed`), never by the
          stylesheet. No script, no arming, content simply shows.
       2. Skipped entirely under reduced motion.
       3. A backstop timer reveals everything after 2s regardless, so a
          throttled or never-firing observer cannot strand the section. */
  function initReveal() {
    var groups = document.querySelectorAll('[data-reveal]');
    if (!groups.length) return;

    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    groups.forEach(function (group) {
      group.classList.add('is-armed');
    });

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    groups.forEach(function (group) { observer.observe(group); });

    setTimeout(function () {
      groups.forEach(function (group) { group.classList.add('is-in'); });
    }, 2000);
  }

  /* Icon buttons that carry both a resting and an active glyph swap between
     them in place. Both live in the DOM; only `hidden` moves. */
  function swapIcon(button, open) {
    var show = button.querySelector('[data-icon-open]');
    var hide = button.querySelector('[data-icon-close]');
    if (show) show.hidden = open;
    if (hide) hide.hidden = !open;
  }

  /* ------------------------------------------------------ MOBILE NAV */
  function initNavToggle() {
    var toggle = document.querySelector('[data-nav-toggle]');
    var drawer = document.querySelector('[data-nav-drawer]');
    if (!toggle || !drawer) return;

    toggle.setAttribute('aria-expanded', 'false');

    var header = toggle.closest('.s7-header');

    function setNav(open) {
      drawer.classList.toggle('is-open', open);
      /* Lifts the header above the shared scrim. Without it the overlay sits
         over the drawer — the drawer cannot escape the header's stacking
         context on its own — and taps on menu links hit the scrim. */
      if (header) header.classList.toggle('s7-header--nav-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      swapIcon(toggle, open);
      if (open) {
        closeSearch();
        openOverlay(drawer, toggle);
      } else {
        closeOverlay();
      }
    }

    toggle.addEventListener('click', function () {
      setNav(!drawer.classList.contains('is-open'));
    });
  }

  /* ------------------------------------------------- FOOTER COLUMNS
     Footer menu columns ship OPEN and are collapsed here on mobile, per the
     per-column "Open by default on mobile" setting.

     The reverse — shipping them closed and forcing them open with desktop CSS —
     is what broke them: a closed <details> hides its content through the UA's
     ::details-content slot using content-visibility, which `display: grid` on
     the child does not override. The panel kept reporting display:grid while
     painting nothing, so Help and Legal were invisible on desktop.

     Driving the `open` attribute instead means the browser is never asked to
     contradict itself, and with no script every column simply stays open. */
  function initFooterColumns() {
    var cols = document.querySelectorAll('[data-footer-col]');
    if (!cols.length || !window.matchMedia) return;

    var mq = window.matchMedia('(max-width: 900px)');

    function apply() {
      cols.forEach(function (col) {
        col.open = mq.matches ? col.dataset.openMobile === 'true' : true;
      });
    }

    apply();
    /* addEventListener on a MediaQueryList is unsupported in older Safari,
       which only has addListener. */
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else if (mq.addListener) mq.addListener(apply);
  }

  /* --------------------------------------------------- SEARCH DROPDOWN
     The panel holds a real GET form to /search, so it works with this file
     absent entirely. Everything below is enhancement: opening it in place
     instead of navigating, and filling the suggestion list from the Section
     Rendering API.

     Suggestions come back as rendered HTML from sections/predictive-search,
     never as JSON — prices are formatted by Liquid on the server. */
  var searchPanel = null;

  function closeSearch() {
    if (!searchPanel || searchPanel.hidden) return;
    searchPanel.hidden = true;
    var toggle = document.querySelector('[data-search-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      swapIcon(toggle, false);
    }
  }

  function initSearch() {
    var toggle = document.querySelector('[data-search-toggle]');
    var panel = document.querySelector('[data-search-panel]');
    if (!toggle || !panel) return;

    searchPanel = panel;

    var input = panel.querySelector('[data-search-input]');
    var results = panel.querySelector('[data-search-results]');
    var timer = null;
    var lastQuery = '';
    var seq = 0;

    function open() {
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      swapIcon(toggle, true);
      if (input) input.focus();
    }

    toggle.addEventListener('click', function () {
      if (panel.hidden) open(); else closeSearch();
    });

    /* Escape closes from anywhere inside the panel and hands focus back to the
       control that opened it, rather than dropping it on <body>. */
    panel.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Escape') return;
      closeSearch();
      toggle.focus();
    });
    toggle.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape') closeSearch();
    });

    if (!input || !results || !window.routes || !window.routes.predictive_search_url) return;

    function suggest(term) {
      /* seq guards against out-of-order responses: a slow request for "sh"
         must not overwrite the results already rendered for "shorts". */
      var mine = ++seq;
      var url = window.routes.predictive_search_url +
        '?q=' + encodeURIComponent(term) +
        '&resources[type]=product&resources[limit]=5' +
        '&section_id=predictive-search';

      fetch(url)
        .then(function (res) { return res.ok ? res.text() : ''; })
        .then(function (html) {
          if (mine !== seq || panel.hidden) return;
          results.innerHTML = html;
        })
        .catch(function () {
          /* Silent. The form underneath still submits to the full results
             page, which is the answer to a failed suggestion request. */
        });
    }

    input.addEventListener('input', function () {
      var term = input.value.trim();
      if (timer) clearTimeout(timer);

      if (term.length < 2) {
        seq++;
        results.innerHTML = '';
        lastQuery = '';
        return;
      }
      if (term === lastQuery) return;
      lastQuery = term;

      timer = setTimeout(function () { suggest(term); }, 250);
    });
  }

  /* Dismissal lives at document level and is registered once, not per init.
     The Theme Editor re-runs initSearch on every header edit, and re-binding
     these there would stack a listener per edit, each holding a detached
     panel. They read the live `searchPanel` instead. */
  function initSearchDismiss() {
    function outside(ev) {
      if (!searchPanel || searchPanel.hidden) return;
      var toggle = document.querySelector('[data-search-toggle]');
      if (searchPanel.contains(ev.target)) return;
      if (toggle && toggle.contains(ev.target)) return;
      closeSearch();
    }

    document.addEventListener('click', outside);
    document.addEventListener('focusin', outside);
  }

  /* --------------------------------------------- DRAWERS / OVERLAY
     One overlay, one focus trap, one Escape handler — shared by the nav
     drawer, the cart drawer and the size-guide drawer so they can never
     disagree about scroll locking. */
  var activeDrawer = null;
  var lastFocused = null;

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function openOverlay(drawer, trigger) {
    var overlay = document.querySelector('[data-overlay]');
    activeDrawer = drawer;
    lastFocused = trigger || document.activeElement;

    /* The overlay is never display:none — it fades via opacity and
       visibility, both of which transition from a class toggled synchronously.
       An earlier version unhid it and added the class inside rAF; when rAF is
       throttled (background tab, suspended renderer) that left the page
       scroll-locked behind an invisible scrim. */
    if (overlay) overlay.classList.add('is-open');
    document.body.classList.add('s7-scroll-locked');

    var first = drawer.querySelector(FOCUSABLE);
    if (first) first.focus();
  }

  function closeOverlay() {
    var overlay = document.querySelector('[data-overlay]');

    if (activeDrawer) {
      activeDrawer.classList.remove('is-open');
      /* The scrim and Escape close the drawer without going through the
         button, so the button's state — expanded and glyph — is reset here
         rather than in the click handler. */
      var toggle = document.querySelector('[aria-expanded="true"][data-nav-toggle]');
      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        swapIcon(toggle, false);
      }
      /* Drop the header back under the overlay, or it stays above the scrim
         for the cart and size-guide drawers opened afterwards. */
      var hdr = document.querySelector('.s7-header--nav-open');
      if (hdr) hdr.classList.remove('s7-header--nav-open');
    }
    if (overlay) overlay.classList.remove('is-open');
    document.body.classList.remove('s7-scroll-locked');

    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    activeDrawer = null;
    lastFocused = null;
  }

  function initOverlay() {
    var overlay = document.querySelector('[data-overlay]');
    if (overlay) overlay.addEventListener('click', closeOverlay);

    document.addEventListener('keydown', function (ev) {
      if (!activeDrawer) return;

      if (ev.key === 'Escape') {
        ev.preventDefault();
        closeOverlay();
        return;
      }

      if (ev.key !== 'Tab') return;

      var items = Array.prototype.filter.call(
        activeDrawer.querySelectorAll(FOCUSABLE),
        function (el) { return el.offsetParent !== null; }
      );
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (ev.shiftKey && document.activeElement === first) {
        ev.preventDefault();
        last.focus();
      } else if (!ev.shiftKey && document.activeElement === last) {
        ev.preventDefault();
        first.focus();
      }
    });

    document.addEventListener('click', function (ev) {
      var closer = ev.target.closest('[data-drawer-close]');
      if (closer) {
        ev.preventDefault();
        closeOverlay();
      }
    });
  }

  /* Exposed so the cart and size-guide drawers reuse the same trap. */
  window.s7Drawer = { open: openOverlay, close: closeOverlay };

  /* --------------------------------------------------------- BOOT */
  function init() {
    initHeaderState();
    measureHeader();
    measureAboveHero();
    initNavToggle();
    initFooterColumns();
    initSearch();
    initSearchDismiss();
    initOverlay();
    initAnnouncements();
    initReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Fallback for browsers without ResizeObserver. */
  if (!('ResizeObserver' in window)) {
    window.addEventListener('resize', function () {
      measureHeader();
      measureAboveHero();
    }, { passive: true });
  }

  /* Theme Editor: sections re-render without a page load. */
  document.addEventListener('shopify:section:load', function () {
    if (headerResizeObserver) {
      headerResizeObserver.disconnect();
      headerResizeObserver = null;
    }
    if (aboveHeroObserver) {
      aboveHeroObserver.disconnect();
      aboveHeroObserver = null;
    }
    initHeaderState();
    measureHeader();
    measureAboveHero();
    initNavToggle();
    initFooterColumns();
    /* Not initSearchDismiss — its listeners are document-level and already
       bound; they follow the new panel through `searchPanel`. */
    initSearch();
    initAnnouncements();
    initReveal();
  });
})();
